import React, { FC, memo } from 'react';
import { StoreSearchViewModel } from '~/lib/data-contract';
import styles from './stores-list.module.css';
import { Typography, Button } from '~/shared/components';
import { useTranslation } from '~/shared/utils';
import { getOpeningHourForToday } from '../p170-store-overview-page.component';
import clsx from 'clsx';

type SharedProps = {
    onSelectStore?: React.Dispatch<React.SetStateAction<StoreSearchViewModel | null>>;
    selectedStore?: StoreSearchViewModel | null;
};

type StoreItemProps = SharedProps & {
    store: StoreSearchViewModel;
};

const StoreItem: FC<StoreItemProps> = memo(({ store, onSelectStore, selectedStore }) => {
    const isSelected = selectedStore?.id === store.id;
    const { storeName, city, address, email, phone, zipcode, url } = store;
    const { translate } = useTranslation();

    const openingHourForToday = getOpeningHourForToday(store);

    const handleSelectStore = () => {
        onSelectStore?.(isSelected ? null : store);
    };

    return (
        <li className={clsx(styles.listItem, { [styles.selected]: isSelected })}>
            <button className={styles.storeNameButton} onClick={handleSelectStore}>
                <Typography variant="body" className={styles.storeName}>
                    {storeName}
                </Typography>
            </button>
            <div className={styles.cardGrid}>
                <button onClick={handleSelectStore} className={styles.storeInfo}>
                    <Typography variant="bodySm">
                        {address},<br />
                        {zipcode} {city}
                    </Typography>
                    <Typography variant="bodySm">
                        {openingHourForToday?.isClosed ? (
                            translate('stores.overview.openingHoursClosed')
                        ) : (
                            <>
                                {translate('stores.overview.openingHoursOpen')}:{' '}
                                {openingHourForToday?.openingTime} -{' '}
                                {openingHourForToday?.closingTime}
                            </>
                        )}
                    </Typography>
                </button>
                <button onClick={handleSelectStore} className={styles.contactInfo}>
                    {phone && <Typography variant="bodySm">{phone}</Typography>}
                    {email && <Typography variant="bodySm">{email}</Typography>}
                </button>
                <div className={styles.buttonWrapper}>
                    <Button variant="secondary" hoverEffect href={url}>
                        {translate('stores.overview.storeLinkText')}
                    </Button>
                </div>
            </div>
        </li>
    );
});

type Props = SharedProps & {
    stores?: StoreSearchViewModel[];
    isFetching?: boolean;
    shouldSortAlphabetically?: boolean;
};

const StoresList: FC<Props> = memo(({ stores, shouldSortAlphabetically, isFetching, ...rest }) => {
    const sortedStores = shouldSortAlphabetically
        ? [...(stores || [])].sort((a, b) => (a.city ?? '').localeCompare(b.city ?? ''))
        : stores;

    const { translate } = useTranslation();

    if (!stores?.length && !isFetching) {
        return (
            <Typography variant="body" className={styles.noResults}>
               {translate('stores.overview.noResults')}
            </Typography>
        );
    }

    return (
        <ul className={styles.list}>
            {sortedStores?.map((store) => (
                <StoreItem store={store} key={store.id} {...rest} />
            ))}
        </ul>
    );
});

StoresList.displayName = 'StoresList';
export default StoresList;
